import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { LazyMotion, domAnimation, m } from 'framer-motion';

import { CONFIG } from 'src/global-config';
import { SvgColor } from 'src/components/svg-color';
import { Carousel, useCarousel, CarouselArrowBasicButtons } from 'src/components/carousel';
import { useLocale as useLocaleHook } from 'src/locales';

// ✅ FIXED: Custom Image that forwards title + alt to <img>
function Image({ src, alt, title, sx, priority, ...other }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      title={title} // ✅ now visible when you inspect
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
      sx={sx}
      {...other}
    />
  );
}

// ----------------------------------------------------------------------
// Motion Variants
const textVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const cardVariant = {
  hidden: { opacity: 0, scale: 0.9, y: 30 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

// ----------------------------------------------------------------------

export function Trucks({ sx, ...other }) {
  const { t } = useLocaleHook();
  const lang = localStorage.getItem('i18nextLng') || 'ar';

  // ✅ Country data with translated content
  const countries = [
    {
      id: 1,
      name: t('Traila'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/flatbed-trailer-2axles.webp`,
      firstHeading: t('Flatbed_Trailer_2_Axles'),
      secondHeading: t('Trailer_length_up_to_13.5_m'),
      thirdHeading: t('Max_load_25_tons'),
      alt: t('alt_for_img1'),
      title: t('meta_for_img1'),
    },
    {
      id: 2,
      name: t('Traila'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/flatbed-trailer-3axles.webp`,
      firstHeading: t('Flatbed_Trailer_3_Axles'),
      secondHeading: t('Trailer_length_up_to_13.5_m'),
      thirdHeading: t('Max_load_30_tons'),
      alt: t('alt_for_img2'),
      title: t('meta_for_img2'),
    },
    {
      id: 3,
      name: t('Traila'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/curtain-trailer.webp`,
      firstHeading: t('Curtain_Sided_Trailer'),
      secondHeading: t('Trailer_length_up_to_13.5_m'),
      thirdHeading: t('Max_load_25_tons'),
      alt: t('alt_for_img3'),
      title: t('meta_for_img3'),
    },
    {
      id: 4,
      name: t('Traila'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/high-sided-trailer.webp`,
      firstHeading: t('High_Sided_Trailer'),
      secondHeading: t('Trailer_length_up_to_15_m'),
      thirdHeading: t('Max_load_20_tons'),
      alt: t('alt_for_img4'),
      title: t('meta_for_img4'),
    },
    {
      id: 5,
      name: t('Traila'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/german-sided-trailer.webp`,
      firstHeading: t('Short_Sided_Trailer_German_Type'),
      secondHeading: t('Trailer_length_up_to_13.5_m'),
      thirdHeading: t('Max_load_25_tons'),
      alt: t('alt_for_img5'),
      title: t('meta_for_img5'),
    },
    {
      id: 6,
      name: t('Traila'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/traila/refrigerated-trailer.webp`,
      firstHeading: t('Refrigerated_Trailer'),
      secondHeading: t('Trailer_length_up_to_13.5_m'),
      thirdHeading: t('Max_load_20_tons'),
      alt: t('alt_for_img6'),
      title: t('meta_for_img6'),
    },
    {
      id: 7,
      name: t('Semi_Trailer_SAX'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/six/sax-high-sided.webp`,
      firstHeading: t('High_Sided_Semi_Trailer_SAX'),
      secondHeading: t('Length_up_to_8_m'),
      thirdHeading: t('Max_load_13_tons'),
      alt: t('alt_for_img7'),
      title: t('meta_for_img7'),
    },
    {
      id: 8,
      name: t('Semi_Trailer_SAX'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/six/sax-refrigerated.webp`,
      firstHeading: t('Refrigerated_Semi_Trailer_SAX'),
      secondHeading: t('Length_up_to_8_m'),
      thirdHeading: t('Max_load_10_tons'),
      alt: t('alt_for_img8'),
      title: t('meta_for_img8'),
    },
    {
      id: 9,
      name: t('Lorry_Medium_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/lorry/Lorry-Cage-Truck-7M.webp`,
      firstHeading: t('Open_Lorry'),
      secondHeading: t('Length_up_to_7.5_m'),
      thirdHeading: t('Max_load_8_tons'),
      alt: t('alt_for_img9'),
      title: t('meta_for_img9'),
    },
    {
      id: 10,
      name: t('Lorry_Medium_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/lorry/closed-lorry.webp`,
      firstHeading: t('Closed_Box_Lorry'),
      secondHeading: t('Length_up_to_6.5_m'),
      thirdHeading: t('Max_load_6_tons'),
      alt: t('alt_for_img10'),
      title: t('meta_for_img10'),
    },
    {
      id: 11,
      name: t('Lorry_Medium_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/lorry/refrigerated-lorry.webp`,
      firstHeading: t('Refrigerated_Lorry'),
      secondHeading: t('Length_up_to_6.5_m'),
      thirdHeading: t('Max_load_6_tons'),
      alt: t('alt_for_img11'),
      title: t('meta_for_img11'),
    },
    {
      id: 12,
      name: t('Lorry_Medium_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/lorry/crane-lorry-5t.webp`,
      firstHeading: t('Crane_Lorry_5T'),
      secondHeading: t('Length_up_to_6.5_m'),
      thirdHeading: t('Max_load_8_tons'),
      alt: t('alt_for_img12'),
      title: t('meta_for_img12'),
    },
    {
      id: 13,
      name: t('Lorry_Medium_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/lorry/crane-lorry-7t.webp`,
      firstHeading: t('Crane_Lorry_7T'),
      secondHeading: t('Length_up_to_6.5_m'),
      thirdHeading: t('Max_load_8_tons'),
      alt: t('alt_for_img13'),
      title: t('meta_for_img13'),
    },
    {
      id: 14,
      name: t('Dyna_Truck_Light_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/dyna/open-dyna.webp`,
      firstHeading: t('Open_Dyna_Truck'),
      secondHeading: t('Length_up_to_6_m'),
      thirdHeading: t('Max_load_4_tons'),
      alt: t('alt_for_img14'),
      title: t('meta_for_img14'),
    },
    {
      id: 15,
      name: t('Dyna_Truck_Light_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/dyna/closed-dyna.webp`,
      firstHeading: t('Closed_Dyna_Truck'),
      secondHeading: t('Length_up_to_6_m'),
      thirdHeading: t('Max_load_3.5_tons'),
      alt: t('alt_for_img15'),
      title: t('meta_for_img15'),
    },
    {
      id: 16,
      name: t('Dyna_Truck_Light_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/dyna/refrigerated-dyna.webp`,
      firstHeading: t('Refrigerated_Dyna_Truck'),
      secondHeading: t('Length_up_to_6_m'),
      thirdHeading: t('Max_load_3.5_tons'),
      alt: t('alt_for_img16'),
      title: t('meta_for_img16'),
    },
    {
      id: 17,
      name: t('Dyna_Truck_Light_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/dyna/crane-dyna.webp`,
      firstHeading: t('Crane_Dyna_Truck'),
      secondHeading: t('Length_up_to_6_m'),
      thirdHeading: t('Max_load_3.5_tons'),
      alt: t('alt_for_img17'),
      title: t('meta_for_img17'),
    },
    {
      id: 18,
      name: t('Pickup_Truck'),
      coverUrl: `${CONFIG.assetsDir}/assets/images/home/truck-images/pickup/Pickup-Truck.webp`,
      firstHeading: t('Pickup_Truck'),
      secondHeading: t('Length_up_to_5_m'),
      thirdHeading: t('Max_load_2_tons'),
      alt: t('alt_for_img18'),
      title: t('meta_for_img18'),
    },
  ];

  const carousel = useCarousel({
    slidesToShow: { xs: 1, sm: 2, md: 'auto' },
    slideSpacing: '32px',
  });

  return (
    <LazyMotion features={domAnimation}>
      <Box
        component="section"
        sx={[
          {
            overflow: 'hidden',
            position: 'relative',
            py: { xs: 5, md: 15 },
            pb: { xs: 5, md: 0 },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <SvgColor
          src={`${CONFIG.assetsDir}/assets/illustrations/illustration-map.svg`}
          sx={{
            width: 780,
            height: 646,
            left: -64,
            top: '50%',
            zIndex: -1,
            opacity: 0.64,
            position: 'absolute',
            color: 'text.disabled',
            transform: 'translateY(-50%)',
            display: { xs: 'none', md: 'block' },
          }}
        />

        <Container>
          <m.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
            <Grid container columnSpacing={3} sx={{ justifyContent: 'space-between' }}>
              {/* Left Text Section */}
              <Grid size={{ xs: 12, md: 4 }}>
                <m.div variants={textVariant}>
                  <Box sx={{ textAlign: { xs: 'center', md: 'unset' } }}>
                    <Typography variant="h2" component="h2">
                      {t('Our-Diverse-Fleet-Built-for-Every-Load')}
                    </Typography>

                    <Typography sx={{ mt: 3, mb: 5, color: 'text.secondary' }}>
                      {t(
                        'The_right_vehicle_for_every_requirement_from_last-mile_delivery_to_GCC-wide_heavy_haulage_Precision-matched_to_your_cargo_and_schedules'
                      )}
                    </Typography>
                  </Box>
                </m.div>
              </Grid>

              {/* Right Carousel Section */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    width: { md: 'calc(50vw + 120px)' },
                  }}
                >
                  <Carousel carousel={carousel} sx={{ pb: 5, pt: { xs: 5, md: 0 } }}>
                    {countries?.map((country, index) => (
                      <m.div key={country?.id || index} variants={cardVariant}>
                        <JobItem country={country} />
                      </m.div>
                    ))}
                  </Carousel>

                  <CarouselArrowBasicButtons {...carousel.arrows} options={carousel?.options} />
                </Box>
              </Grid>
            </Grid>
          </m.div>
        </Container>
      </Box>
    </LazyMotion>
  );
}

// ----------------------------------------------------------------------

const transition = (theme) =>
  theme.transitions.create(['opacity', 'transform'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.short,
  });

// ✅ JobItem with working title and alt
function JobItem({ country, sx, ...other }) {
  return (
    <Card
      sx={[
        (theme) => ({
          width: { md: 280 },
          '&:hover': {
            boxShadow: theme.vars.customShadows.z16,
            '& img': { transform: 'scale(1.06)' },
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    >
      <Box sx={{ overflow: 'hidden' }}>
        <Image
          alt={country?.alt || country?.title || country?.name}
          title={country?.title || country?.name}
          src={country?.coverUrl}
          sx={{ transition, objectFit: 'contain' }}
          priority
        />
      </Box>

      <Box sx={{ textAlign: 'center', p: 2.5 }}>
        <Typography variant="h6" component="h6">
          {country?.name}
        </Typography>

        {country?.firstHeading && (
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1 }}>
            {country?.firstHeading}
          </Typography>
        )}

        {country?.secondHeading && (
          <Typography variant="body2" color="text.secondary">
            {country?.secondHeading}
          </Typography>
        )}

        {country?.thirdHeading && (
          <Typography variant="body2" color="text.secondary">
            {country?.thirdHeading}
          </Typography>
        )}
      </Box>
    </Card>
  );
}
